import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import TableShimmer from "components/Shared/TableShimmer"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Badge,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import { filters } from "components/Common/constants"
import Switch from "components/Shared/Switch"
import { showAlert } from "store/reducers/alertReducer"
import DeleteModal from "components/Common/DeleteModal"
import { getAllBranchRatings } from "store/actions/ratingsAction"
import { getAllBranchs } from "store/actions/usersAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { EXPORT_DATA } from "helpers/url_helper"
import { pdfDownload } from "helpers/api_helper"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import { useTranslation } from "react-i18next"

/** Shows up to 2 lines; full feedback appears in a hover tooltip when truncated. */
function FeedbackTooltipCell({ text }) {
  const value = String(text ?? "").trim() || "-"
  const textRef = useRef(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPos, setTooltipPos] = useState(null)

  useEffect(() => {
    const measure = () => {
      const el = textRef.current
      if (!el) return
      setShowTooltip(el.scrollHeight > el.clientHeight + 1)
    }

    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [value])

  useEffect(() => {
    if (!tooltipPos) return undefined
    const hide = () => setTooltipPos(null)
    window.addEventListener("scroll", hide, true)
    return () => window.removeEventListener("scroll", hide, true)
  }, [tooltipPos])

  const hideTooltip = () => setTooltipPos(null)

  const revealTooltip = () => {
    if (!showTooltip || value === "-") return
    const el = textRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const maxWidth = 360
    const left = Math.min(
      Math.max(12, rect.left + rect.width / 2 - maxWidth / 2),
      window.innerWidth - maxWidth - 12
    )
    setTooltipPos({
      top: Math.max(12, rect.top - 10),
      left,
      maxWidth,
    })
  }

  return (
    <td style={{ minWidth: 280, maxWidth: 360, verticalAlign: "top" }}>
      <div
        ref={textRef}
        onMouseEnter={revealTooltip}
        onMouseLeave={hideTooltip}
        onFocus={revealTooltip}
        onBlur={hideTooltip}
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
      {tooltipPos ? (
        <div
          role="tooltip"
          style={{
            position: "fixed",
            top: tooltipPos.top,
            left: tooltipPos.left,
            maxWidth: tooltipPos.maxWidth,
            transform: "translateY(-100%)",
            zIndex: 2000,
            padding: "8px 10px",
            borderRadius: 6,
            background: "#2b2f36",
            color: "#fff",
            fontSize: 12,
            lineHeight: 1.4,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
            pointerEvents: "none",
          }}
        >
          {value}
        </div>
      ) : null}
    </td>
  )
}

const BranchRatings = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t(`Branch Ratings | Sign Burger`)

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const branchIdParam = searchParams.get("branchId")
  const minDate = searchParams.get("minDate")
  const maxDate = searchParams.get("maxDate")

  const {
    loading,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    isLoading,
    branchRatings,
  } = useSelector(state => state.Ratings)

  const { branchsList } = useSelector(state => state.Users)

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
    branchId: branchIdParam
      ? branchIdParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
  })
  const [selectedId, setSelectedId] = useState(null)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [dateRange, setDateRange] = useState({ min: minDate, max: maxDate })
  const flatpickrRef = useRef(null)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      27,
      "rating_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [userRole])

  useEffect(() => {
    fetchBranchRating()
    fetchAllBranchs()
  }, [])

  const fetchBranchRating = () => {
    const { sortOrder, branchId } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: sortOrder,
      keyword: searchTerm,
      branchIds: branchIds,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllBranchRatings(payload))
    setIsApiCalled(true)
  }

  const fetchAllBranchs = () => {
    dispatch(getAllBranchs())
  }

  const fetchBranchRatings = (page = 1) => {
    const { sortOrder, branchId } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []

    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    if (branchId.length > 0) {
      const branchIdString = branchId
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("branchId", branchIdString)
    }

    if (dateRange.min && dateRange.max) {
      searchParams.set("minDate", dateRange.min?.toString())
      searchParams.set("maxDate", dateRange.max?.toString())
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      branchIds: branchIds,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    dispatch(getAllBranchRatings(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/branch-ratings?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    fetchBranchRatings(1)
  }

  const onPageChange = page => {
    fetchBranchRatings(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 0 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "branchId",
      type: "multiSelect",
      options: [
        ...branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
      ],
      placeholder: t("Select Branches"),
    },
  ]

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      orderBy: 1,
      keyword: "",
      branchIds: [],
      dateRange: {},
    }
    dispatch(getAllBranchRatings(payload))
    navigate(`/branch-ratings?page=1`)
    setSearchTerm("")
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
    setSelectedFilters({
      sortOrder: 1,
      branchId: [],
    })
  }

  const exportToCSV = async () => {
    const { branchId } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []

    const payload = {
      keyword: searchTerm || "",
      type: "RATINGS",
      exportType: "csv",
      branchId: branchIds,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Branch Ratings"))
    }
  }

  const handleExcelDownload = async () => {
    const { branchId } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []

    const payload = {
      keyword: searchTerm || "",
      type: "RATINGS",
      exportType: "xlsx",
      branchId: branchIds,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Branch Ratings"))
    }
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Branch Ratings")}
                isExportData={loading ? false : true}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                  searchBy={t("Name, Email & Phone Number")}
                  colSize={4}
                />
                <div>
                  {loading ? (
                    <TableShimmer row={7} col={7} />
                  ) : branchRatings.length === 0 ? (
                    <p className="text-center my-2">
                      {t("No branch rating found")}
                    </p>
                  ) : (
                    <div className="table-responsive">
                      <Table
                        className="table mb-0"
                        hover
                        style={{ minWidth: 1100 }}
                      >
                        <thead className="table-light full_th">
                          <tr>
                            <th>{t("S. No.")}</th>
                            <th>{t("Branch Name")}</th>
                            <th>{t("First Name")}</th>
                            <th>{t("Last Name")}</th>
                            <th>{t("Email")}</th>
                            <th>{t("Phone Number")}</th>
                            <th>{t("Dob")}</th>
                            <th>{t("Platform")}</th>
                            <th style={{ minWidth: 280 }}>{t("Feedback")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {branchRatings.map((item, index) => (
                            <tr
                              key={item._id}
                              onClick={() => {
                                navigate(
                                  `/branch-ratings/branch-rating-details?id=${item._id}`
                                )
                              }}
                              className="cursor-pointer"
                            >
                              <th scope="row">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </th>
                              <td>
                                {language === "sa"
                                  ? item?.branchNameAr
                                  : item?.branchName}
                              </td>
                              <td>{item?.userInfo?.name || "-"}</td>
                              <td>{item?.userInfo?.lastName || "-"}</td>
                              <td>{item?.userInfo?.email}</td>
                              <td>{`${item?.userInfo?.phoneCode} ${item?.userInfo?.phoneNumber}`}</td>
                              <td>{item?.userInfo?.dob || "-"}</td>
                              <td>{t(item?.platform) || "-"}</td>
                              <FeedbackTooltipCell text={item?.feedback} />
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
                {!loading && branchRatings.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={branchRatings}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default BranchRatings
